import { Controller } from "@hotwired/stimulus";

const SELECTED_CLASSES = [
  "!bg-default-500",
  "!text-white",
  "[&>svg]:inline-block",
];

const INDETERMINATE_CLASSES = [
  "!bg-default-600",
  "!text-white",
  "[&>svg]:inline-block",
];

export default class extends Controller {
  static targets = [
    "bottomBar",
    "hintBar",
    "counterNumber",
    "counterLabel",
    "launcherButton",
    "messageButton",
    "contactsButton",
    "selectAllCheckbox",
    "batchActionButton",
    "groupPickerButton",
  ];
  static values = { selectedIds: [] };

  connect() {
    const itemLabelNode = this.element.querySelector("[data-item-label]");
    this.itemLabel = itemLabelNode?.dataset.itemLabel || "élément";
    this.renderElements();
    this.updateSelectAllState();

    // The dropdown component portals its content to document.body, which moves
    // menu items outside this controller's DOM scope. Stimulus can't connect
    // data-action bindings across that boundary, so we catch them globally.
    this._handlePortaledClick = (e) => {
      const el = e.target.closest(
        '[data-action*="openMessageDrawer"], [data-action*="openContactsModal"], [data-action*="openGroupPickerModal"]',
      );
      if (!el || this.element.contains(el)) return;

      e.preventDefault();
      const synthetic = { preventDefault() {}, currentTarget: el };

      if (el.dataset.action.includes("openMessageDrawer")) {
        this.openMessageDrawer(synthetic);
      } else if (el.dataset.action.includes("openContactsModal")) {
        this.openContactsModal(synthetic);
      } else if (el.dataset.action.includes("openGroupPickerModal")) {
        this.openGroupPickerModal(synthetic);
      }
    };
    document.addEventListener("click", this._handlePortaledClick);
  }

  disconnect() {
    if (this._handlePortaledClick) {
      document.removeEventListener("click", this._handlePortaledClick);
    }
  }

  buildLauncherUrl() {
    if (!this.hasLauncherButtonTarget) return;

    const { launcherButtonTarget } = this;
    const href = launcherButtonTarget.getAttribute("href");
    const url = new URL(href, window.location.origin);
    url.searchParams.delete("ids[]");
    [...this.selectedIdsValue].forEach((id) => {
      url.searchParams.append("ids[]", id);
    });
    launcherButtonTarget.setAttribute("href", url.toString());
  }

  buildContactsUrl() {
    if (!this.hasContactsButtonTarget) return;

    const { contactsButtonTarget } = this;
    const href = contactsButtonTarget.getAttribute("href");
    const url = new URL(href, window.location.origin);
    url.searchParams.delete("recipient_ids[]");
    url.searchParams.set(
      "return_to",
      `${window.location.pathname}${window.location.search}`,
    );

    [...this.selectedIdsValue].forEach((id) => {
      const checkbox = document.querySelector(`[data-selectable-id="${id}"]`);
      const userIdElement = checkbox?.closest("[data-user-id]");
      const userId = userIdElement?.dataset.userId;
      if (userId) {
        url.searchParams.append("recipient_ids[]", userId);
      }
    });

    contactsButtonTarget.setAttribute("href", url.toString());
  }

  openMessageDrawer(event) {
    event.preventDefault();

    const sourceHref =
      event.currentTarget.getAttribute("href") ||
      (this.hasMessageButtonTarget
        ? this.messageButtonTarget.getAttribute("href")
        : null);
    if (!sourceHref) return;

    const baseUrl = new URL(sourceHref, window.location.origin);

    // Add template_id from button data attribute
    const templateId = event.currentTarget.dataset.messageTemplateId;
    if (templateId) {
      baseUrl.searchParams.append("message_template_id", templateId);
    }

    // Collect recipient_ids from selected checkboxes
    [...this.selectedIdsValue].forEach((id) => {
      const checkbox = document.querySelector(`[data-selectable-id="${id}"]`);
      const userIdElement = checkbox?.closest("[data-user-id]");
      const userId = userIdElement?.dataset.userId;
      if (userId) {
        baseUrl.searchParams.append("recipient_ids[]", userId);
      }
    });

    // Visit drawer
    if (window.Turbo) {
      window.Turbo.visit(baseUrl.toString(), { frame: "drawer" });
    }
  }

  openContactsModal(event) {
    event.preventDefault();

    this.buildContactsUrl();

    const sourceHref =
      event.currentTarget.getAttribute("href") ||
      (this.hasContactsButtonTarget
        ? this.contactsButtonTarget.getAttribute("href")
        : null);
    if (!sourceHref) return;

    const baseUrl = new URL(sourceHref, window.location.origin);

    // Use Turbo to visit the modal frame
    if (window.Turbo) {
      window.Turbo.visit(baseUrl.toString(), { frame: "modal" });
    }
  }

  openGroupPickerModal(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const href = button.getAttribute("href");
    if (!href) return;

    const baseUrl = new URL(href, window.location.origin);

    // Selected ids are already the contactable row ids (profile_id /
    // location_id / point_of_sale_id) — `data-selectable-id` on the row
    // checkbox. The contactable_type is baked into the picker URL by the
    // toolbar, so we only need to pass the ids here.
    [...this.selectedIdsValue].forEach((id) => {
      baseUrl.searchParams.append("contactable_ids[]", id);
    });

    if (window.Turbo) {
      window.Turbo.visit(baseUrl.toString(), { frame: "modal" });
    }
  }

  submitBatchAction(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const baseUrl = new URL(button.getAttribute("href"), window.location.origin);
    const method = button.dataset.turboMethod || "patch";

    // Add selected IDs to URL
    [...this.selectedIdsValue].forEach((id) => {
      baseUrl.searchParams.append("ids[]", id);
    });

    // Create and submit a form with the correct method
    const form = document.createElement("form");
    form.method = "POST";
    form.action = baseUrl.toString();
    form.style.display = "none";

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    if (csrfToken) {
      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "authenticity_token";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    // Add method override for PATCH/DELETE
    if (method !== "post") {
      const methodInput = document.createElement("input");
      methodInput.type = "hidden";
      methodInput.name = "_method";
      methodInput.value = method;
      form.appendChild(methodInput);
    }

    document.body.appendChild(form);
    form.submit();
  }

  renderElements() {
    const count = this.selectedIdsValue.length;

    if (this.hasCounterNumberTarget) {
      this.counterNumberTarget.innerHTML = count;
    }

    if (this.hasCounterLabelTarget) {
      this.counterLabelTarget.innerHTML =
        count > 1
          ? `${this.itemLabel}s sélectionnés`
          : `${this.itemLabel} sélectionné`;
    }

    if (this.hasBottomBarTarget) {
      if (count <= 0) {
        this.bottomBarTarget.classList.add("hidden");
      } else {
        this.bottomBarTarget.classList.remove("hidden");
      }
    }

    if (this.hasHintBarTarget) {
      if (count <= 0) {
        this.hintBarTarget.classList.remove("hidden");
      } else {
        this.hintBarTarget.classList.add("hidden");
      }
    }

    this.buildLauncherUrl();
    this.buildContactsUrl();
    this.updateSelectAllState();
  }

  toggleSelection(event) {
    event.preventDefault();
    event.stopPropagation();

    const id = event.target.dataset.selectableId;

    let newSelection;
    if (!this.selectedIdsValue.includes(id)) {
      newSelection = [...this.selectedIdsValue, id];
      event.target.classList.add(...SELECTED_CLASSES);
    } else {
      newSelection = this.selectedIdsValue.filter(
        (selectedId) => String(selectedId) !== String(id),
      );
      event.target.classList.remove(...SELECTED_CLASSES);
    }

    this.selectedIdsValue = newSelection;
    this.renderElements();
  }

  isSelected(id) {
    return this.selectedIdsValue.some((selectedId) => selectedId === id);
  }

  toggleAll(event) {
    const checkboxes = this.element.querySelectorAll("[data-selectable-id]");
    const total = checkboxes.length;
    const selected = this.selectedIdsValue.length;

    // If all are selected, deselect all; otherwise select all
    if (selected === total) {
      this.deselectAll();
    } else {
      this.selectAll();
    }
  }

  selectAll() {
    const checkboxes = this.element.querySelectorAll("[data-selectable-id]");
    const ids = Array.from(checkboxes).map((cb) => cb.dataset.selectableId);

    ids.forEach((id) => {
      const checkbox = document.querySelector(`[data-selectable-id="${id}"]`);
      if (checkbox) {
        checkbox.classList.add(...SELECTED_CLASSES);
      }
    });

    this.selectedIdsValue = ids;
    this.renderElements();
  }

  deselectAll() {
    this.selectedIdsValue.forEach((id) => {
      const checkbox = document.querySelector(`[data-selectable-id="${id}"]`);
      if (checkbox) {
        checkbox.classList.remove(...SELECTED_CLASSES);
      }
    });

    this.selectedIdsValue = [];
    this.renderElements();
  }

  updateSelectAllState() {
    if (!this.hasSelectAllCheckboxTarget) return;

    const checkboxes = this.element.querySelectorAll("[data-selectable-id]");
    const total = checkboxes.length;
    const selected = this.selectedIdsValue.length;

    const selectAll = this.selectAllCheckboxTarget;
    const checkIcon = selectAll.querySelector("svg[data-icon='check']");
    const minusIcon = selectAll.querySelector("svg[data-icon='minus']");

    // Remove all states first
    selectAll.classList.remove(...SELECTED_CLASSES);
    selectAll.classList.remove(...INDETERMINATE_CLASSES);

    if (total === 0) {
      // No checkboxes - unchecked state
      if (checkIcon) checkIcon.style.display = "none";
      if (minusIcon) minusIcon.style.display = "none";
    } else if (selected === 0) {
      // None selected - unchecked state
      if (checkIcon) checkIcon.style.display = "none";
      if (minusIcon) minusIcon.style.display = "none";
    } else if (selected === total) {
      // All selected - checked state
      selectAll.classList.add(...SELECTED_CLASSES);
      if (checkIcon) checkIcon.style.display = "inline-block";
      if (minusIcon) minusIcon.style.display = "none";
    } else {
      // Some selected - indeterminate state
      selectAll.classList.add(...INDETERMINATE_CLASSES);
      if (checkIcon) checkIcon.style.display = "none";
      if (minusIcon) minusIcon.style.display = "inline-block";
    }
  }
}
